import { createHOC } from 'vue-hoc';
import keyBy from 'lodash/keyBy';
import gql from 'graphql-tag';
import get from 'lodash/get';
// import clone from "lodash/clone";

import withUser from '~lib/common/hoc/withUser';
import userManagement from '~lib/common/providers/userManagement';
import casesService from '~lib/common/providers/casesService';
import fetchOrgData from '~lib/common/utils/fetchOrgData';

import GradingReports from './GradingReports';
// import auth from "../../../../lib/common/providers/auth";
import gradingClient from '../../../providers/gradingClient';
import assignmentQueries from '../../../graphql/queries/Assignment';
import attemptQueries from '../../../graphql/queries/Attempt';

import getAssignmentUserIds from '../../../utils/getAssignmentUserIds';

// function parseGroupMetadata(group) {
//   try {
//     return JSON.parse(group.description);
//   } catch (e) {
//     if (e.name === "SyntaxError") {
//       return {};
//     }
//     throw e;
//   }
// }

const options = {
  name: 'GradingReportsContainer',
  props: {
    id: {
      type: String,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    idToken: {
      type: String,
    },
  },
  data() {
    return {
      state: {
        loading: false,
        searching: false,
        reports: [],
        allReports: [],
        error: null,
        caseConf: null,
        assignment: null,
        user: null,
      },
    };
  },
  computed: {
    reportsByUserId() {
      return keyBy(this.state.reports, 'user.user_id');
    },
  },
  async created() {
    try {
      this.state.loading = true;
      const user = this.user;
      const groups = user['https://vrpatients.com/groups'];
      const orgId = groups[0];
      const caseId = this.$router.currentRoute.params.id;
      const userId = this.$router.currentRoute.query.userId;
      const { assignmentId } = this.$router.currentRoute.query;

      const [reportsResponse, users, assignment, attempts] = await Promise.all([
        gradingClient.get('/reports', {
          filter: {
            caseId,
            userId,
            assignmentId,
          },
        }),
        userManagement.getOrgMembers(orgId, true),
        assignmentId ? this.getAssignment(assignmentId) : Promise.resolve(null),
        assignmentId ? this.getAttempts(assignmentId) : Promise.resolve([])
      ]);
      this.state.caseConf = {
        id: caseId,
        name: get(assignment, 'caseName') || this.$router.currentRoute.query.caseName,
      };
      const usersById = keyBy(users, 'user_id');
      this.state.reports = reportsResponse.data.gradingreports;
      this.state.reports = this.state.reports.map(report => ({
        ...report,
        user: usersById[report.userId],
      }));
      this.state.assignment = assignment;
      this.state.user = userId ? usersById[userId] : null;
      this.state.attempts = attempts;
      if (assignment) {
        const orgData = await fetchOrgData(this.user);
        const assignmentUserIds = await getAssignmentUserIds(
          assignment,
          orgData.groups
        );
        // Create stub reports for users in the assignment that don't have grading reports yet
        const userIdsWithoutReports = assignmentUserIds.filter(userId => {
          const user = usersById[userId];
          if (!user) {
            return false;
          }
          // Instructors/admins might be part of groups but we don't want to display stub reports for them
          if (user.app_metadata.role !== 'STUDENT') {
            return false;
          }
          return !this.reportsByUserId[userId];
        });
        this.state.reports = this.state.reports.concat(
          userIdsWithoutReports.map(userId => ({
            isStub: true,
            user: usersById[userId],
            userId,
          }))
        );
        // Set the throughGroup property on the reports that are assigned through groups
        if (assignment.students.groups) {
          assignment.students.groups.forEach(groupId => {
            const group = orgData.groups.find(grp => grp._id === groupId);
            if (group && group.members) {
              group.members.forEach(memberId => {
                const memberReport = this.reportsByUserId[memberId];
                if (!memberReport || !memberReport.user) {
                  return;
                }
                memberReport.throughGroup = group;
              });
            }
          });
        }
      }
    } catch (e) {
      console.error(e);
      this.state.error = e;
    } finally {
      this.state.loading = false;
    }
  },
  methods: {
    async getAssignment(assignmentId) {
      try {
        const response = await casesService.query({
          query: gql(assignmentQueries.fetchById),
          variables: {
            id: assignmentId,
          },
          fetchPolicy: 'network-only',
        });
        return response.data.assignments[0];
      } catch (e) {
        console.error(e);
        this.state.error = e;
      }
    },
    async getAttempts(assignmentId) {
      try {
        const response = await casesService.query({
          query: gql(attemptQueries.fetchByAssignmentId),
          variables: {
            assignmentId,
          },
          fetchPolicy: 'network-only',
        });
        return response.data.attempts;
      } catch (e) {
        console.error(e);
        this.state.error = e;
      }
    },
    async addAssignmentAttempts(user) {
      try {
        this.state.loading = true;
        const { assignment } = this.state;
        const updatedAssignment = {
          ...assignment,
          additionalAttempts: (assignment.additionalAttempts || []).concat({
            principalType: 'USER',
            principalId: user.user_id,
            count: 1,
          })
        }
        await casesService.mutate({
          mutation: gql(assignmentQueries.save),
          variables: {
            value: updatedAssignment,
          },
          fetchPolicy: 'no-cache',
        });
        this.state.assignment = updatedAssignment;
      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }
    }
  },
};

const renderWith = {
  props: {
    loading() {
      return this.state.loading;
    },
    searching() {
      return this.state.searching;
    },
    reports() {
      return this.state.reports.map(report => {
        try {
          return {
            ...report,
            caseData: JSON.parse(report.caseData)
          }
        } catch (e) {
          if (e.name === 'SyntaxError') {
            return report;
          }
          throw e;
        }
      });
    },
    error() {
      return this.state.error;
    },
    caseConf() {
      return this.state.caseConf;
    },
    assignment() {
      return this.state.assignment;
    },
    user() {
      return this.state.user;
    },
    attempts() {
      return this.state.attempts;
    },
    groupByCase() {
      return !this.$router.currentRoute.params.id;
    },
    groupByUser() {
      return !this.$router.currentRoute.query.userId;
    }
  },
  listeners: {
    addAssignmentAttempts(user) {
      this.addAssignmentAttempts(user);
    }
  },
};

const GradingReportsContainer = createHOC(GradingReports, options, renderWith);
export default withUser()(GradingReportsContainer);
